import { apiClient } from './axios';

import type {
  CreateStockSourceDto,
  StockEngineResult,
  StockSourceConfig,
  StockSourceQuery,
  UpdateStockSourceDto,
} from '@repo/shared-types';

// Rows come back with the cookie stripped and a cookie_set flag added.
export interface StockSourceRow {
  id: string;
  name: string;
  description: string | null;
  config: StockSourceConfig;
  cookie_set: boolean;
  is_active: boolean;
  sync_interval_minutes: number;
  status: 'idle' | 'syncing' | 'needs_auth' | 'error';
  last_sync_started_at: string | null;
  last_sync_completed_at: string | null;
  last_sync_status: string | null;
  last_sync_count: number | null;
  created_at: string;
  updated_at: string;
}

export interface StockSyncRunRow {
  id: string;
  stock_source_id: string;
  status: 'running' | 'success' | 'failed' | 'auth_expired';
  trigger: 'manual' | 'scheduled';
  pages_fetched: number;
  records_parsed: number;
  upserted: number;
  marked_unavailable: number;
  error: string | null;
  started_at: string;
  finished_at: string | null;
}

export interface StockItemRow {
  id: string;
  external_id: string;
  make: string | null;
  model: string | null;
  trim: string | null;
  year: number | null;
  price: number | null;
  mileage: number | null;
  fuel_type: string | null;
  transmission: string | null;
  body_type: string | null;
  colour: string | null;
  detail_url: string | null;
  image_url: string | null;
  is_available: boolean;
  data: Record<string, unknown>;
  last_seen_at: string;
}

export interface StockSyncState {
  source: StockSourceRow;
  running: boolean;
  enriching_images: boolean;
  latest_run: StockSyncRunRow | null;
  items: { available: number; unavailable: number; missing_images: number };
}

export type StockTestResult = StockEngineResult & { field_coverage: Record<string, string> };

export interface Paginated<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const stockSyncApi = {
  getAll: async (query?: StockSourceQuery) => {
    const res = await apiClient.get<StockSourceRow[]>('/stock-sources', { params: query });
    return res.data;
  },
  getOne: async (id: string) => {
    const res = await apiClient.get<StockSourceRow>(`/stock-sources/${id}`);
    return res.data;
  },
  create: async (data: CreateStockSourceDto) => {
    const res = await apiClient.post<StockSourceRow>('/stock-sources', data);
    return res.data;
  },
  update: async (id: string, data: UpdateStockSourceDto) => {
    const res = await apiClient.patch<StockSourceRow>(`/stock-sources/${id}`, data);
    return res.data;
  },
  remove: async (id: string) => {
    await apiClient.delete(`/stock-sources/${id}`);
  },
  /** Crawl up to 3 pages with an unsaved config — no DB writes. */
  test: async (data: { config: StockSourceConfig; cookie_header?: string; max_pages?: number }) => {
    const res = await apiClient.post<StockTestResult>('/stock-sources/test', data);
    return res.data;
  },
  triggerSync: async (id: string) => {
    const res = await apiClient.post<{ started: boolean; message: string }>(
      `/stock-sources/${id}/sync`,
    );
    return res.data;
  },
  cancelSync: async (id: string) => {
    const res = await apiClient.post<{ cancelling: boolean; message: string }>(
      `/stock-sources/${id}/sync/cancel`,
    );
    return res.data;
  },
  enrichImages: async (id: string, all = false) => {
    const res = await apiClient.post<{ started: boolean; message: string }>(
      `/stock-sources/${id}/enrich-images`,
      undefined,
      { params: all ? { all: 'true' } : undefined },
    );
    return res.data;
  },
  syncState: async (id: string) => {
    const res = await apiClient.get<StockSyncState>(`/stock-sources/${id}/sync-state`);
    return res.data;
  },
  runs: async (id: string, page = 1, limit = 20) => {
    const res = await apiClient.get<Paginated<StockSyncRunRow>>(`/stock-sources/${id}/runs`, {
      params: { page, limit },
    });
    return res.data;
  },
  items: async (id: string, page = 1, limit = 20, search?: string) => {
    const res = await apiClient.get<Paginated<StockItemRow>>(`/stock-sources/${id}/items`, {
      params: { page, limit, ...(search ? { search } : {}) },
    });
    return res.data;
  },
};
